import { useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, ApiError } from '../api/client.js';

const ME_KEY = ['me'];
const VIEW_AS_KEY = 'uwp-view-as';

// Module-level store for `viewAs`. Lives outside React so every useAuth()
// consumer shares one value — without this, each call would get its own
// useState and TopNav's toggle wouldn't notify AppLayout's banner until a
// reload. Storage events handle cross-tab sync; the local listener set
// handles same-tab fan-out.
const viewAsStore = (() => {
  let value = (() => {
    try { return localStorage.getItem(VIEW_AS_KEY) || null; }
    catch { return null; }
  })();
  const listeners = new Set();
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key !== VIEW_AS_KEY) return;
      value = e.newValue || null;
      listeners.forEach(l => l());
    });
  }
  return {
    get: () => value,
    set: (next) => {
      const v = next || null;
      if (v === value) return;
      value = v;
      try {
        if (v) localStorage.setItem(VIEW_AS_KEY, v);
        else localStorage.removeItem(VIEW_AS_KEY);
      } catch {}
      listeners.forEach(l => l());
    },
    subscribe: (l) => { listeners.add(l); return () => listeners.delete(l); },
  };
})();

// Roles a real admin can preview the UI as. Any other value resolves to
// the real role (no-op). Exported so the navigation can render a picker.
export const EMULATABLE_ROLES = ['crew', 'foreman', 'bookkeeper'];

// Single source of truth for the current user. /api/me returns 401 when
// unauthed; we map that to `null` and let route guards bounce to /login.
//
// View-as emulation: a real admin can set `viewAs` to any role in
// EMULATABLE_ROLES to preview that role's UI. Server still trusts the
// real role on every request — privileged calls keep working — so this
// is purely a client-side render hint. Useful gotcha: when emulating
// `foreman`, the supervised-user list comes from the admin's own
// foreman_user_ids (typically empty), so supervised-only surfaces like
// CrewRosterPanel will render their empty state. Emulation tells you
// "what does this role see in the chrome", not "what data would they get"
// — the server still returns admin-scoped data.
export function useAuth() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const body = await get('/api/me');
        return body.user || null;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const viewAs = useSyncExternalStore(viewAsStore.subscribe, viewAsStore.get, viewAsStore.get);
  const setViewAs = viewAsStore.set;

  const realRole = query.data?.role || null;
  const realIsAdmin = realRole === 'admin';
  // Only real admins get to emulate. Any non-admin with a stale viewAs
  // value in localStorage is ignored.
  const effectiveRole = realIsAdmin
      && EMULATABLE_ROLES.includes(viewAs)
      && viewAs !== realRole
    ? viewAs
    : realRole;

  const isAdmin     = effectiveRole === 'admin';
  const isForeman   = effectiveRole === 'foreman';
  const isBookkeeper = effectiveRole === 'bookkeeper';
  const isPayrollRole = isAdmin || isBookkeeper;
  const isAdminSurface = isAdmin || isForeman || isBookkeeper;

  // Sanitize viewAs as it's exposed — only return a value the UI should act on.
  const emulating = realIsAdmin && EMULATABLE_ROLES.includes(viewAs) && viewAs !== realRole;

  return {
    user: query.data,
    isLoading: query.isLoading,
    isAuthed: !!query.data,
    role: effectiveRole,
    realRole,
    isAdmin,
    isRealAdmin: realIsAdmin,
    isForeman,
    isBookkeeper,
    isPayrollRole,
    isAdminSurface,
    foremanUserIds: query.data?.foreman_user_ids || [],
    viewAs: emulating ? viewAs : null,
    isEmulating: emulating,
    setViewAs,
    refetch: query.refetch,
    logout: async () => {
      await post('/api/auth/logout').catch(() => {});
      viewAsStore.set(null);
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  };
}
